/* eslint-disable no-unused-vars */

import { useState, useEffect } from "react";

import { Helmet } from "react-helmet";
import Select from "react-select";
import DataTable from "react-data-table-component";
import { Link } from "react-router-dom";

/* MATERIAL TAILWIND COMPONENTS */
import { Button, Tooltip } from "@material-tailwind/react";

/* ICONS */
import { IoEyeSharp, IoBan, IoToggleSharp } from "react-icons/io5";
import { BiEdit } from "react-icons/bi";
import { FiTrash } from "react-icons/fi";

/* DATA */
import NoImage from "../../../assets/images/no-image.png";

/* USER DEFINED COMPONENTS */
import Input from "../../../components/input";

/* TOAST NOTIFICATION */
import { toast } from "react-toast";

/* API IMPORT */
import {
  deleteProduct,
  editProductStatusById,
  getProductList,
} from "../../../service/api/product.api";
import { IMAGE_URL } from "../../../config";
import { handleAddToken } from "../../../service/request";
import { useSelector } from "react-redux";

/* CATEGORIES */
const categoryOptions = [
  {
    label: "Breakfast, cereals and premixes",
    value: "Breakfast, cereals and premixes",
  },
  {
    label: "Chocolate, cookies and candies",
    value: "Chocolate, cookies and candies",
  },
  {
    label: "Grains, legumes and flours",
    value: "Grains, legumes and flours",
  },
  {
    label: "Healthy Snacks",
    value: "Healthy Snacks",
  },
];

/* STATUS */
const status = [
  { label: "Active", value: "Active" },
  { label: "Inactive", value: "Inactive" },
];

const Inventory = () => {
  
  // GLOBAL VARIABLES
  const userDetails = useSelector((state) => state?.user);
  // STATE VARIABLES
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [sizePerPage, setSizePerPage] = useState(30);

  const [productsList, setProductsList] = useState([]);

  const [totalDocs, setTotalDocs] = useState([]);

  const [search, setSearch] = useState("");

  const [deleteId, setDeleteId] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);

  /* FUNCTIONS */
  const handlePageChange = async (e) => {
    setPage(e);
    getProductsListFunction();
  };

  const handlePerPageChange = async (e) => {
    setSizePerPage(e);
    getProductsListFunction();
  };

  const getProductsListFunction = async () => {
    await handleAddToken(userDetails?.token);
    setLoading(true);
    const params = {
      page: page,
      sizePerPage: sizePerPage,
      pagination: true,
    };
    if (search) {
      params.search = search;
    }

    getProductList(params)
      .then((res) => {
        setProductsList(res?.data?.docs);
        setTotalDocs(res?.data?.totalDocs);
      })
      .catch((e) => {
        console.log("e-->", e);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  /* HANDLE DELETE DATA */
  const handleDelete = (deleteId) => {
    deleteProduct(deleteId)
      .then((res) => {
        toast.success(res?.message);
      })
      .catch((e) => {
        toast.error(e?.response?.data?.message);
      })
      .finally(() => {
        setDeleteModal(!deleteModal);
        setDeleteId(null);
        getProductsListFunction();
      });
  };
  const handleStatusChange = (productId, status) => {
    editProductStatusById(productId, status)
      .then((res) => {
        toast.success(res?.message);
      })
      .catch((e) => {
        toast.error(e?.response?.data?.message);
      })
      .finally(() => {
        getProductsListFunction();
      });
  };

  /* TABLE COLUMNS */
  const columns = [
    {
      name: "Image",
      width: "15vh",
      selector: (row) => (
        <span>
          <img
            src={
              row?.image?.front?.url
                ? IMAGE_URL + row?.image?.front?.url
                : NoImage
            }
            className={`${
              row?.image?.front?.url ? "w-20 h-20" : "w-16 h-16 opacity-30"
            }  rounded-lg object-cover`}
            alt={row.name}
          />
        </span>
      ),
    },
    {
      name: "Name",
      grow: 2,
      selector: (row) => (
        <span className="text-sm font-proxima-bold text-black">{row.name}</span>
      ),
    },
    {
      name: "SKU",
      selector: (row) => (
        <span className="text-sm font-proxima-semibold text-black text-opacity-60">
          {row.sku}
        </span>
      ),
    },
    {
      name: "Category",
      grow: 2,
      selector: (row) => (
        <span className="text-sm font-proxima-semibold text-black text-opacity-60">
          {row?.category?.map((element, index) => {
            return index === 0 ? (
              <span>{element?.name}</span>
            ) : (
              <span>{", " + element?.name}</span>
            );
          })}
        </span>
      ),
    },
    {
      name: "Quantity",

      selector: (row) => (
        <span className="text-sm font-proxima-bold text-black">
          {row.quantity}
        </span>
      ),
    },
    {
      name: "Visibility",

      selector: (row) => (
        <span
          className={
            row?.status === "active"
              ? "text-sm font-proxima-bold text-green-800 text-opacity-60"
              : "text-sm font-proxima-bold text-yellow-700 text-opacity-60"
          }
        >
          {row.status === "active" ? "Enabled" : "Disabled"}
        </span>
      ),
    },
    {
      name: "Actions",
      grow: 2,
      selector: (row) => (
        <div className="flex items-center">
          <Tooltip
            content="View product details"
            className="text-sm font-proxima-semibold"
          >
            <Link to={"/inventory/view-product/" + row?._id}>
              <Button className="p-2 rounded-full shadow-none hover:shadow-none bg-transparent hover:bg-black hover:bg-opacity-5">
                <IoEyeSharp className="w-5 h-5 text-greyText" />
              </Button>
            </Link>
          </Tooltip>
          <Tooltip
            content="Edit product details"
            className="text-sm font-proxima-semibold"
          >
            <Link to={"/inventory/edit-product/" + row?._id}>
              <Button className="p-2 rounded-full shadow-none hover:shadow-none bg-transparent hover:bg-black hover:bg-opacity-5">
                <BiEdit className="w-5 h-5 text-greyText" />
              </Button>
            </Link>
          </Tooltip>
          <Tooltip
            content="Remove product"
            className="text-sm font-proxima-semibold"
          >
            <Button
              onClick={() => {
                handleDelete(row?._id);
              }}
              className="p-2 rounded-full shadow-none hover:shadow-none bg-transparent hover:bg-black hover:bg-opacity-5"
            >
              <FiTrash className="w-5 h-5 text-greyText" />
            </Button>
          </Tooltip>
          <Tooltip
            content="Disable on website"
            className="text-sm font-proxima-semibold"
          >
            {row?.status === "active" ? (
              <Button
                onClick={() => {
                  handleStatusChange(row?._id, {status: "inactive"});
                }}
                className="p-2 rounded-full shadow-none hover:shadow-none bg-transparent hover:bg-black hover:bg-opacity-5"
              >
                <IoBan className="w-5 h-5 text-greyText" />
              </Button>
            ) : (
              <Button
                onClick={() => {
                  handleStatusChange(row?._id,{status: "active"});
                }}
                className="p-2 rounded-full shadow-none hover:shadow-none bg-transparent hover:bg-black hover:bg-opacity-5"
              >
                <IoToggleSharp className="w-5 h-5 text-greyText" />
              </Button>
            )}
          </Tooltip>
        </div>
      ),
    },
  ];

  /* USE-EFFECTS */
  useEffect(() => {
    getProductsListFunction();
  }, [page, sizePerPage, search]);

  /* LOADING SCREEN FOR DATATABLE */
  const LoadingScreen = () => {
    return (
      <div className="w-full bg-[#F5F5F5] bg-opacity-50 rounded-md border border-authBorder py-5">
        <p className="text-sm font-proxima-semibold text-black text-center">
          Please wait!
        </p>
        <p className="text-sm font-proxima-semibold text-black text-center">
          We are fetching data{" "}
        </p>
      </div>
    );
  };

  const NoDataScreen = () => {
    return (
      <div className="w-full bg-[#F5F5F5] bg-opacity-50 rounded-md border border-authBorder py-5">
        <p className="text-sm font-proxima-semibold text-black text-center">
          There are no queries to display.
        </p>
      </div>
    );
  };
  return (
    <section className="px-0 py-2 xl:p-10 lg:p-10 min-h-screen xl:min-h-full lg:min-h-full">
      {/* PAGE TITLE */}
      <Helmet>
        <title>Inventory | CIDA Perfumes</title>
      </Helmet>

      <div className="container-full-width">
        <div className="block xl:flex lg:flex items-center justify-between flex-shrink-0 mx-auto">
          {/* HEADING */}
          <div>
            <h1 className="text-3xl font-proxima-bold text-black tracking-tight">
              Inventory
            </h1>
            <h2 className="text-lg font-proxima-regular text-greyText tracking-tight">
              Manage the product control on your website
            </h2>
          </div>

          {/* SEARCH AND FILTERS */}
          <div className="block xl:flex lg:flex items-center gap-2">
            {/* SEARCHBAR */}
            <div className="w-full xl:w-searchBar lg:w-searchBar mt-2 xl:mt-0 lg:mt-0">
              <Input
                type="text"
                placeholder="Search from your inventory"
                name="searchInv"
                id="searchInv"
                onChange={(e) => setSearch(e?.target?.value)}
                value={search}
              />
            </div>

            {/* CATEGORY DROPDOWN */}
            <div className="w-full xl:w-statusSelect lg:w-statusSelect mt-2 xl:mt-0 lg:mt-0 hidden">
              <Select
                options={categoryOptions}
                classNamePrefix="dropdown"
                placeholder="Select category"
                name="category"
                id="category"
              />
            </div>

            {/* STATUS DROPDOWN */}
            <div className="w-full xl:w-statusSelect lg:w-statusSelect mt-2 xl:mt-0 lg:mt-0">
              <Select
                options={status}
                classNamePrefix="dropdown"
                placeholder="Select status"
                name="status"
                id="status"
              />
            </div>

            {/* ADD PRODUCT */}
            <Link to="/inventory/add-product">
              <Button className="w-full xl:w-auto lg:w-auto mt-2 xl:mt-0 lg:mt-0 bg-logoYellow rounded-lg py-3 px-8 text-sm font-proxima-semibold shadow-none hover:shadow-none normal-case hover:bg-opacity-80 transition-all duration-300 ease-in-out">
                Add Product
              </Button>
            </Link>
          </div>
        </div>

        {/* INVENTORY TABLE */}
        <div className="mt-5">
          <DataTable
            className="data-table"
            columns={columns}
            data={totalDocs > 0 ? productsList : []}
            progressPending={loading}
            progressComponent={<LoadingScreen />}
            pagination
            paginationServer
            paginationTotalRows={totalDocs}
            onChangeRowsPerPage={handlePerPageChange}
            onChangePage={handlePageChange}
            noDataComponent={<NoDataScreen />}
          />
        </div>
      </div>
    </section>
  );
};

export default Inventory;
